import { memo, useCallback, useEffect, useState } from "react";
import dynamic from "next/dynamic";

import { api } from "@utils/api";
import { useApp } from "@context/AppProvider";

import List from "./Card/List";
import Form from "./Card/Form";
const SelectPaymentOptionModal = dynamic(() =>
  import("@models/SelectPaymentOption.modal")
);

const orderTotal = (subTotal, totalShipping, voucher) => {
  let val = subTotal + totalShipping;
  if (voucher && voucher.discount) {
    val = val - voucher.discount;
  }

  return val.toFixed(2);
};

const getPromoType = (v) => {
  if (v.type === "fixedAmount") {
    return `$${v.discount.toFixed(2)} Off`;
  } else if (v.type === "freeShippingFixed") {
    return `Shipping - $${v.discount.toFixed(2)} Off`;
  } else if (v.type === "freeShippingPercentageOff") {
    return `Shipping - ${v.value}% Off`;
  } else if (v.type === "giftCard") {
    return `Gift Card - $${v.discount.toFixed(2)} Off`;
  } else if (v.type === "percentageOff") {
    return `${v.value}% Off`;
  }
};

const isMinCharge = (promo) => {
  let val = false;
  if (promo) {
    let payableAmount = promo.orderTotal - promo.value;
    if (payableAmount > 0 && payableAmount < 1) {
      val = true;
    }
  }

  return val;
};

const Payment = (props) => {
  const {
    idb: { currency },
    convertCurrency,
  } = useApp();
  const {
    styles,
    subTotal,
    totalShipping,
    tax,
    totalGst,
    session,
    user,
    updatePaymentSource,
    voucher,
    checkout = true,
    isGuest = false,
    state = null,
  } = props;
  const [card, setCard] = useState({
    list: [],
    item: null,
    type: null,
    view: false,
  });

  const loadCards = useCallback(
    async (data) => {
      let id = data ? data.id : null;
      if (isGuest) {
        if (data?.token?.card) {
          setCard((val) => ({
            ...val,
            item: { ...data.token.card, new: true },
            type: null,
            list: [data?.token?.card],
          }));
          updatePaymentSource(data.token);
        } else {
          setCard((val) => ({
            ...val,
            item: { ...(state?.card?.card || {}), new: true },
            type: null,
            list: state?.card?.card ? [state.card.card] : [],
          }));
          updatePaymentSource(state.card);
        }
      } else if (data && data.id && data.new) {
        setCard((val) => ({
          ...val,
          item: { ...data, new: true },
          type: null,
          list: [...val.list, data],
        }));
        updatePaymentSource(data);
      } else {
        const { status, data } = await api(
          {
            url: "/user/card-list",
            method: "GET",
          },
          session
        );
        const list = status ? data : [];
        const item = list.length
          ? id
            ? list.find((i) => i.id === id)
            : list[0]
          : {};
        updatePaymentSource(item);
        setCard((val) => ({
          ...val,
          item,
          type: null,
          list,
        }));
      }
    },
    [isGuest, session, state?.card, updatePaymentSource]
  );

  const showPaymentModal = () => {
    setCard((val) => ({
      ...val,
      view: true,
    }));
  };

  const selectCard = useCallback(
    (id) => {
      const item = card.list.find((i) => i.id === id);
      updatePaymentSource(item);
      setCard((val) => ({
        ...val,
        item,
        view: false,
      }));
    },
    [card.list, updatePaymentSource]
  );

  const close = () => {
    setCard((val) => ({
      ...val,
      view: false,
    }));
  };

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  const editCard = (type) => {
    setCard((val) => ({
      ...val,
      type,
    }));
  };
  return (
    <>
      <div className={styles.paymentDetailsCart}>
        <h2>Payment Details</h2>
        {card.type !== null || !card.list.length ? (
          <Form
            styles={styles}
            item={card.type === "edit" ? card.item : { name: "" }}
            type={card.type}
            editCard={editCard}
            user={user}
            session={session}
            loadCards={loadCards}
            count={card.list.length}
            checkoutAsGuest={isGuest}
          />
        ) : (
          <List
            styles={styles}
            card={card.item}
            editCard={editCard}
            showPaymentModal={showPaymentModal}
            isGuest={isGuest}
          />
        )}
      </div>
      {checkout && (
        <div className={styles.paymentCardBox}>
          <div className={styles.paymentSubtotalList}>
            <ul>
              <li>
                Cart Subtotal <span>${subTotal.toFixed(2)}</span>
              </li>

              <li>
                Shipping <span>${totalShipping.toFixed(2)}</span>
              </li>
              {voucher && !!voucher.discount && (
                <>
                  <li>
                    <div>
                      Promo Code
                      <br />({getPromoType(voucher)})
                    </div>
                    <span className="text-danger">
                      -${voucher.discount.toFixed(2)}
                    </span>
                  </li>
                </>
              )}
            </ul>
          </div>
          <div className={styles.paymentTotalList}>
            <ul>
              <li className={styles.orderTotal}>
                Order Total{" "}
                <span>AUD ${orderTotal(subTotal, totalShipping, voucher)}</span>
              </li>
              {currency !== "aud" && (
                <p>
                  This is approximately{" "}
                  {convertCurrency(
                    orderTotal(subTotal, totalShipping, voucher),
                    false,
                    true,
                    true
                  )}
                </p>
              )}
              <li>
                Includes {tax?.label || "GST"}{" "}
                <span>${totalGst.toFixed(2)}</span>
              </li>

              {isMinCharge(voucher) && (
                <li className="text-danger">*Minimum Charge $1.00</li>
              )}
            </ul>
          </div>
        </div>
      )}
      {!!card.view && (
        <SelectPaymentOptionModal
          cards={card.list}
          card={card.item}
          onChange={(id) => selectCard(id)}
          editCard={editCard}
          close={close}
        />
      )}
    </>
  );
};

export default memo(Payment);
